import type { PageParams } from '@/domain/entities/paging'

/** 7 tipe diskon yang didukung backend. */
export const DISCOUNT_TYPES = [
  'percent',
  'amount',
  'buy_x_get_y',
  'coupon',
  'discount_after_amount',
  'special_price',
  'progressive',
] as const
export type DiscountType = (typeof DISCOUNT_TYPES)[number]

/** Level tempat diskon boleh dipakai kasir. */
export const DISCOUNT_SCOPES = ['transaction', 'item', 'both'] as const
export type DiscountScope = (typeof DISCOUNT_SCOPES)[number]

/**
 * Tier untuk diskon bertingkat (`progressive`).
 * `max_amount` null berarti tier ini tidak punya batas atas (tier tertinggi).
 */
export interface DiscountTier {
  min_amount: number
  max_amount: number | null
  type: 'percent' | 'amount'
  value: number
}

export interface SpecialPriceItem {
  target_product_sku: string
  special_price: number
}

export interface BuyXGetYItem {
  buy_product_sku: string
  buy_qty: number
  get_product_sku: string
  get_qty: number
  get_discount_percent: number
  is_multiple?: boolean
}

/**
 * Parameter kompleks per tipe diskon yang disimpan sebagai JSONB di backend.
 * Hanya field yang relevan dengan tipe aktif yang perlu diisi.
 */
export interface DiscountRules {
  /** Minimal total belanja agar diskon aktif (coupon, discount_after_amount). */
  min_purchase_amount?: number | null
  /** Tipe potongan diskon: percent atau amount (coupon, discount_after_amount). */
  discount_type?: 'percent' | 'amount' | null
  /** Batas maksimal total pemakaian kupon seluruh tenant (coupon). */
  usage_limit?: number | null
  /** SKU produk yang harus dibeli (buy_x_get_y). */
  buy_product_sku?: string | null
  /** Daftar SKU produk yang memenuhi syarat dibeli (buy_x_get_y). */
  buy_product_skus?: string[] | null
  /** Jumlah minimum produk yang harus dibeli (buy_x_get_y). */
  buy_qty?: number | null
  /** SKU produk yang didapatkan gratis/diskon (buy_x_get_y). */
  get_product_sku?: string | null
  /** Daftar SKU produk yang memenuhi syarat didapatkan (buy_x_get_y). */
  get_product_skus?: string[] | null
  /** Jumlah produk yang didapatkan (buy_x_get_y). */
  get_qty?: number | null
  /** Persentase diskon pada produk yang didapat — 100 = gratis (buy_x_get_y). */
  get_discount_percent?: number | null
  /** Apakah promo berlaku kelipatan (buy_x_get_y). */
  is_multiple?: boolean
  /** Daftar aturan pasangan Beli X Gratis Y (buy_x_get_y). */
  buy_x_get_y_items?: BuyXGetYItem[] | null
  /** SKU produk yang mendapat harga promo (special_price). */
  target_product_sku?: string | null
  /** Harga promo pengganti harga normal (special_price). */
  special_price?: number | null
  /** Daftar item harga promo (special_price). */
  special_price_items?: SpecialPriceItem[] | null
  /** Tier belanja bertingkat (progressive). */
  tiers?: DiscountTier[] | null
}

/** Master diskon milik satu Tenant, bisa berlaku di semua atau beberapa outlet. */
export interface Discount {
  readonly id: string
  readonly tenantId: string
  readonly name: string
  readonly type: DiscountType
  readonly value: number
  /**
   * Batas atas potongan, hanya berlaku untuk `percent` / `discount_after_amount`.
   * Null = tanpa batas.
   */
  readonly maxAmount: number | null
  /** Kode promo unik per tenant. Diisi hanya untuk tipe `coupon`. */
  readonly couponCode: string | null
  /** true = diskon berlaku di seluruh outlet tenant. */
  readonly isAllOutlets: boolean
  /** UUID outlet yang ditugaskan, diisi hanya jika `isAllOutlets = false`. */
  readonly outletIds: string[]
  /** Parameter tambahan spesifik tipe diskon. */
  readonly rules: DiscountRules
  readonly scope: DiscountScope
  /** Kasir wajib mengetik alasan saat memakai diskon ini. */
  readonly requireReason: boolean
  readonly active: boolean
}

/** Payload buat/ubah. `active` punya jalurnya sendiri (DELETE & restore). */
export interface DiscountInput {
  readonly name: string
  readonly type: DiscountType
  readonly value: number
  readonly maxAmount: number | null
  readonly couponCode: string | null
  readonly isAllOutlets: boolean
  readonly outletIds: string[]
  readonly rules: DiscountRules
  readonly scope: DiscountScope
  readonly requireReason: boolean
}

export type DiscountListParams = PageParams & {
  readonly active: boolean | null
  /**
   * `?scope=item` di BE ikut memunculkan diskon ber-scope `both`
   * (`scope IN ('item','both')`), begitu juga `transaction`.
   */
  readonly scope: DiscountScope | null
}

/**
 * Aturan yang ditegakkan usecase BE. Semua pelanggarannya dijawab 400 dengan
 * pesan generik "input tidak valid", jadi dicek juga di sini supaya admin tahu
 * aturan mana yang dilanggar.
 */
export function validateDiscount(input: DiscountInput): string | null {
  if (!input.name.trim()) return 'Nama diskon tidak boleh kosong.'
  if (input.value < 0) return 'Nilai diskon tidak boleh negatif.'

  if (input.type === 'percent' && input.value > 100) return 'Diskon persen maksimal 100%.'
  if (input.type === 'amount' && input.maxAmount !== null) {
    return 'Batas maksimal hanya berlaku untuk diskon persen — nilai nominal sudah menjadi batasnya sendiri.'
  }
  if (input.maxAmount !== null && input.maxAmount <= 0) {
    return 'Batas maksimal harus lebih dari 0. Kosongkan kalau memang tanpa batas.'
  }

  if (input.type === 'coupon' && !input.couponCode?.trim()) {
    return 'Tipe Kupon wajib mengisi kode kupon.'
  }
  if (input.type === 'buy_x_get_y') {
    const items = input.rules.buy_x_get_y_items
    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        if (!items[i].buy_product_sku?.trim()) {
          return `Aturan Beli-Dapat ke-${i + 1}: SKU Produk Dibeli wajib diisi.`
        }
        if (!items[i].get_product_sku?.trim()) {
          return `Aturan Beli-Dapat ke-${i + 1}: SKU Produk Hadiah wajib diisi.`
        }
      }
    } else {
      const hasBuy = Boolean(
        input.rules.buy_product_sku?.trim() ||
        (input.rules.buy_product_skus && input.rules.buy_product_skus.length > 0),
      )
      const hasGet = Boolean(
        input.rules.get_product_sku?.trim() ||
        (input.rules.get_product_skus && input.rules.get_product_skus.length > 0),
      )
      if (!hasBuy || !hasGet) {
        return 'Tipe Beli X Gratis Y wajib mengisi SKU produk yang dibeli dan didapat.'
      }
    }
  }
  if (input.type === 'special_price') {
    const items = input.rules.special_price_items
    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        if (!items[i].target_product_sku?.trim()) {
          return `Item Promo ke-${i + 1}: SKU Produk Target wajib diisi.`
        }
        if (!items[i].special_price || items[i].special_price <= 0) {
          return `Item Promo ke-${i + 1}: Harga Promo wajib diisi lebih dari 0.`
        }
      }
    } else {
      if (!input.rules.target_product_sku?.trim()) {
        return 'Tipe Harga Promo wajib mengisi SKU produk target.'
      }
      if (!input.rules.special_price || input.rules.special_price <= 0) {
        return 'Tipe Harga Promo wajib mengisi harga promo lebih dari 0.'
      }
    }
  }
  if (input.type === 'progressive') {
    if (!input.rules.tiers || input.rules.tiers.length === 0)
      return 'Tipe Diskon Bertingkat wajib mengisi minimal satu tier.'
  }
  if (!input.isAllOutlets && input.outletIds.length === 0) {
    return 'Pilih minimal satu outlet, atau aktifkan "Berlaku di semua outlet".'
  }

  return null
}
